// Purpose: Card primitive. surface-elevated bg, border-subtle 1px border,
// rounded-lg, optional header/footer slots. Interactive variant adds
// hover state + cursor-pointer.

import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Increases elevation on hover (for linkable cards). */
  interactive?: boolean;
  /** Padding density. Default 'default' (20px). */
  padding?: 'compact' | 'default' | 'comfortable' | 'none';
  children?: ReactNode;
}

const PADDING: Record<NonNullable<CardProps['padding']>, string> = {
  compact: 'p-4',
  default: 'p-5',
  comfortable: 'p-6',
  none: 'p-0',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { interactive, padding = 'default', className, children, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      className={[
        'bg-surface-elevated border border-border-subtle rounded-lg shadow-md',
        'transition-colors duration-fast ease-out',
        interactive ? 'cursor-pointer hover:border-border-strong' : '',
        PADDING[padding],
        className ?? '',
      ].join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
});

export function CardHeader({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={[
        'mb-4 pb-4 border-b border-border-default',
        'flex items-center justify-between',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </div>
  );
}

export function CardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={['text-heading-m text-fg-primary', className ?? ''].join(' ')}>{children}</h3>
  );
}

export function CardFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={[
        'mt-4 pt-4 border-t border-border-default',
        'flex items-center justify-end gap-2',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </div>
  );
}
